import {
    CATEGORY_NAMING_MAP,
    CATEGORY_SUBCATEGORY_ORDER,
    SUBCATEGORY_NAMING_MAP,
    SUBCATEGORY_KEYWORDS_MAP,
    CATEGORY_KEYWORDS_MAP
} from "../config/use-category-config";

export const normalize = (str) => str?.toLowerCase()?.replace(/[^a-z0-9]/g, '');

export const queryWords = (str) => str.trim().toLowerCase().split(/[\s-]+/).map(normalize);

// export const queryJoined = queryWords.join('');


export const matchesUPC = (product, queryWords) => {
    // return product.upc?.some(upc => {
    return product.upcCode?.some(upc => {
        const upcSegments = upc.toLowerCase().split(/[\s()-]+/).map(normalize);
        return queryWords.every(word =>
            upcSegments.some(segment => segment.includes(word))
        );
    }) ?? false;
};

export const matchesTagEntry = (tagEntry, queryWords, queryJoined) => {

    const tagSegments = tagEntry.toLowerCase().split(/[\s()™,®-]+/).map(normalize);

    const combinedTagSegments = tagSegments.join('');

    return (
        queryWords.every(word =>
            tagSegments.some(segment => segment === word || segment.startsWith(word))
        ) ||
        combinedTagSegments.includes(queryJoined)
    );
};

export const matchesSearchTags = (product, queryWords, queryJoined) => {
    return product.searchTags?.some(tagEntry => matchesTagEntry(tagEntry, queryWords, queryJoined)) ?? false;
};


export const matchesCategory = (product, queryWords, queryJoined) => {
    const mainCatKey = product.category;
    const mainCatNormalized = normalize(CATEGORY_NAMING_MAP[mainCatKey] || mainCatKey);
    const segments = mainCatNormalized.split(/\s+/);

    const matchesIndividualWords = queryWords.every(word =>
        segments.some(segment => segment === word || segment.startsWith(word))
    );

    const matchesCombined = mainCatNormalized.includes(queryJoined);

    return matchesIndividualWords || matchesCombined;
};

export const matchesSubcategory = (product, queryWords, queryJoined) => {
    const subCatKey = product.subcategory || '';
    const subCatNormalized = normalize(SUBCATEGORY_NAMING_MAP[subCatKey] || subCatKey);

    const segments = subCatNormalized.split(/\s+/);

    const matchesIndividualWords = queryWords.every(word =>
        segments.some(segment => segment === word || segment.startsWith(word))
    );

    const matchesCombined = subCatNormalized.includes(queryJoined);

    return matchesIndividualWords || matchesCombined;
};

export const matchesLogos = (product, queryWords, queryJoined) => {
    return product.logos?.some(logo => {
        const normalizedLogo = normalize(logo);
        return queryWords.every(word => normalizedLogo.includes(word)) || normalizedLogo.includes(queryJoined);
    }) ?? false;
};

// MIGHT NEED TO TWEAK LANG LOGIC
export const matchesResourceTitle = (product, queryWords, queryJoined) => {
    return product.sections?.some(section => {
        // const normalizedResourceTitle = normalize(section.resourceTitle);
        const normalizedResourceTitle = normalize(section.resourceTitleEN || section.resourceTitleFR);
        return queryWords.every(word => normalizedResourceTitle.includes(word)) || normalizedResourceTitle.includes(queryJoined);
    }) ?? false;
};

export const matchesColors = (product, queryWords, queryJoined) => {
    // return product.colors?.some(color => {
    return product.colour?.some(color => {
        const normalizedColor = normalize(color);
        return queryWords.every(word => normalizedColor.includes(word)) || normalizedColor.includes(queryJoined);
    }) ?? false;
};

export const matchesAvailability = (product, queryWords) => {
    const availabilityValue = normalize(product.availability || '');
    return queryWords.every(word => availabilityValue.includes(word));
};

export const matchesSubtitle = (product, queryWords, queryJoined) => {
    const subtitleSegments = product.subtitle?.toLowerCase().split(/[\s()™,®-]+/).map(normalize) ?? [];
    const combinedSubtitleSegments = subtitleSegments.join('');

    return queryWords.every(word =>
        subtitleSegments.some(segment => segment === word || segment.startsWith(word))
    ) || combinedSubtitleSegments.includes(queryJoined);
};

export const matchesSpecEntry = (specEntry, queryWords, queryJoined) => {
    const specSegments = specEntry.toLowerCase().split(/[\s()™,®-]+/).map(normalize);
    const combinedSpecSegments = specSegments.join('');

    return queryWords.every(word =>
        specSegments.some(segment => segment === word || segment.startsWith(word))
    ) || combinedSpecSegments.includes(queryJoined);
};

export const matchesSpecs = (product, queryWords, queryJoined) => {
    const matchesSpecList = (list) => list?.some(specEntry => matchesSpecEntry(specEntry, queryWords, queryJoined)) ?? false;

    return matchesSpecList(product.specList1)
        || matchesSpecList(product.specList2)
        || matchesSpecList(product.specList3)
        || matchesSpecList(product.specList4);
};

export const matchesCategoryKeyword = (product, queryWords) => {
    const mainCatKey = product.category;
    const categoryKeywords = CATEGORY_KEYWORDS_MAP[mainCatKey]?.map(normalize) || [];

    return queryWords.every(word =>
        categoryKeywords.some(keyword => keyword.includes(word))
    );
};

export const matchesSubcategoryKeyword = (product, queryWords) => {
    const subCatKey = product.subcategory || '';
    const subcategoryKeywords = SUBCATEGORY_KEYWORDS_MAP[subCatKey]?.map(normalize) || [];

    return queryWords.every(word =>
        subcategoryKeywords.some(keyword => keyword.includes(word))
    );
};

export const matchesProductTitle = (product, queryWords, queryJoined) => {
    const titleValue = product.title || '';
    if (!titleValue) return false;

    const titleSegments = titleValue
        .toLowerCase()
        // .split(/[\s()™,®\/-]+/)
        .split(/[\s()™,®/-]+/) // Removed unnecessary escape
        .map(normalize)
        .filter(Boolean);

    const titleCombined = titleSegments.join('');

    const matchesIndividualWords = queryWords.every(word =>
        titleSegments.some(segment => segment === word || segment.startsWith(word))
    );

    const matchesCombined = titleCombined.includes(queryJoined);

    return matchesIndividualWords || matchesCombined;
};

export const unifiedFilteredProducts = (products, query) => {
    if (!query) return [];

    const queryWords = query.trim().toLowerCase().split(/[\s-]+/).map(normalize);
    const queryJoined = queryWords.join('');

    return products.filter(product => {
        const matchReasons = [];
        if (matchesAvailability(product, queryWords)) matchReasons.push('Availability');
        if (matchesCategory(product, queryWords, queryJoined)) matchReasons.push('Category');
        if (matchesSubcategory(product, queryWords, queryJoined)) matchReasons.push('Sub Category');
        if (matchesSearchTags(product, queryWords, queryJoined)) matchReasons.push('Search Tags');
        if (matchesUPC(product, queryWords)) matchReasons.push('UPC');
        if (matchesLogos(product, queryWords, queryJoined)) matchReasons.push('Logos');
        if (matchesResourceTitle(product, queryWords, queryJoined)) matchReasons.push('Resource Title');
        if (matchesColors(product, queryWords, queryJoined)) matchReasons.push('Colors');
        if (matchesProductTitle(product, queryWords, queryJoined)) matchReasons.push('Product Title');
        if (matchesSubtitle(product, queryWords, queryJoined)) matchReasons.push('Subtitle');
        if (matchesSpecs(product, queryWords, queryJoined)) matchReasons.push('Spec Lists');
        if (matchesCategoryKeyword(product, queryWords)) matchReasons.push('Category Keyword');

        const isMatch = matchReasons.length > 0;

        // if (isMatch) {
        //     console.log(`Matched "${product.title}" for query "${query}" due to: [${matchReasons.join(', ')}]`);
        // }

        return isMatch;
    });
};


export const calculateMatchScore = (product, queryWords, queryJoined) => {
    let score = 0;
    const scoreDetails = [];

    // WORKING
    if (matchesUPC(product, queryWords)) {
        score += 100;
        scoreDetails.push({ reason: 'UPC', points: 100 });
    }
    if (matchesProductTitle(product, queryWords, queryJoined)) {
        score += 90;
        scoreDetails.push({ reason: 'Product Title', points: 90 });
    }
    if (matchesSubtitle(product, queryWords, queryJoined)) {
        score += 80;
        scoreDetails.push({ reason: 'Subtitle', points: 80 });
    }
     if (matchesAvailability(product, queryWords)) {
        score += 5;
        scoreDetails.push({ reason: 'Availability', points: 5 });
    }
    if (matchesColors(product, queryWords, queryJoined)) {
        score += 5;
        scoreDetails.push({ reason: 'Colors', points: 5 });
    }
        if (matchesLogos(product, queryWords, queryJoined)) {
        score += 10;
        scoreDetails.push({ reason: 'Logos', points: 10 });
    }
       if (matchesResourceTitle(product, queryWords, queryJoined)) {
            score += 20;
            scoreDetails.push({ reason: 'Resource Title', points: 20 });
        }

      if (matchesSpecs(product, queryWords, queryJoined)) {
            score += 30;
            scoreDetails.push({ reason: 'Specs', points: 30 });
        }


   if (matchesCategory(product, queryWords, queryJoined)) {
        score += 40;
        scoreDetails.push({ reason: 'Category', points: 40 });
    }
       if (matchesSubcategory(product, queryWords, queryJoined)) {
        score += 35; // Example slightly different weighting for subcategory
        scoreDetails.push({ reason: 'Subcategory', points: 35 });
    }
         if (matchesCategoryKeyword(product, queryWords)) {
        score += 60;
        scoreDetails.push({ reason: 'Category Keyword', points: 60 });
    }

  
    // TURN ALL ON AFTER TESTING




    if (matchesSearchTags(product, queryWords, queryJoined)) {
        score += 70;
        scoreDetails.push({ reason: 'Search Tags', points: 70 });
    }
 
    // if (matchesSubcategoryKeyword(product, queryWords)) {
    //     score += 50;
    //     scoreDetails.push({ reason: 'Subcategory Keyword', points: 50 });
    // }






    // if (score > 0) {
    //     console.log(`Matched "${product.title}" with score ${score}. Reasons:`, scoreDetails);
    // }
    // if (score > 0) {
    //     console.log(`Matched "${product.title}" (${product.category}) with score ${score}. Reasons:`, scoreDetails);
    // }


    return score;
};

// export const calculateMatchScore = (product, queryWords, queryJoined) => {
//     let score = 0;

//     if (matchesUPC(product, queryWords)) score += 100;
//     if (matchesProductTitle(product, queryWords, queryJoined)) score += 90;
//     if (matchesSubtitle(product, queryWords, queryJoined)) score += 80;
//     if (matchesSearchTags(product, queryWords, queryJoined)) score += 70;
//     if (matchesCategoryKeyword(product, queryWords)) score += 60;
//     if (matchesSubcategoryKeyword(product, queryWords)) score += 50;
//     if (matchesCategory(product, queryWords, queryJoined)) score += 40;
//     if (matchesSpecs(product, queryWords, queryJoined)) score += 30;
//     if (matchesResourceTitle(product, queryWords, queryJoined)) score += 20;
//     if (matchesLogos(product, queryWords, queryJoined)) score += 10;
//     if (matchesColors(product, queryWords, queryJoined)) score += 5;
//     if (matchesAvailability(product, queryWords)) score += 5;

//     return score;
// };